<template>
  <div class="view crm customers">
    <div class="page">
      <div class="header">
        <h3 class="text-black mt-2 mb-3">
          <strong>{{ $t('crm.list.customers-management') }}</strong>
        </h3>
      </div>
      <div class="content">
        <div class="card">
          <div class="card-header card-header-flex">
            <div class="d-flex flex-column justify-content-center mr-auto">
              <h5 class="mb-0">
                {{ $t('crm.list.customers-list') }}
                <span :key="meta.total" class="animated fadeIn">({{ meta.total }})</span>
              </h5>
            </div>
            <div class="d-flex flex-column justify-content-center">
              <span>
                <a-button type="default" icon="sync" class="mr-2" @click="fetch">
                  {{ $t('crm.list.refresh') }}
                </a-button>
                <a-button type="primary" icon="user" @click="onNewCustomer">
                  {{ $t('crm.list.new-customer') }}
                </a-button>
              </span>
            </div>
          </div>
          <!-- table -->
          <a-table
            v-show="!loading"
            :dataSource="customers"
            :loading="loading"
            :columns="columns"
            :rowKey="(record, index) => record._id"
            :scroll="{ x: 900 }"
            :pagination="pagination"
            @change="updateQuery"
          >
            <span slot="avatar" slot-scope="avatar">
              <a-badge color="blue" dot>
                <a-avatar shape="square" :size="45" :src="avatar" icon="user" />
              </a-badge>
            </span>

            <span slot="id" slot-scope="uid">
              <strong style="font-size: 1.15rem">{{ uid }}</strong>
            </span>

            <span slot="gender" slot-scope="gender">
              <div class="gender" :class="gender">
                <i v-if="gender === 'male'" class="icmn-man"></i>
                <i v-else-if="gender === 'female'" class="icmn-woman"></i>
                <i v-else class="icmn-man-woman"></i>
              </div>
            </span>

            <span slot="name" slot-scope="customer">
              <span class="name">
                <span class="first_name">
                  {{ customer.name }}
                </span>
                <span class="last_name" v-if="customer.last_name">
                  {{ customer.last_name }}
                </span>
              </span>
            </span>

            <span slot="status" slot-scope="status">
              <span class="font-size-12 badge" :class="statusClass(status)">{{ status }}</span>
            </span>

            <span slot="active" slot-scope="active">
              <span class="dot" :class="{ active: active }"></span>
            </span>

            <span slot="projects" slot-scope="projects">
              {{ projects.length }}
            </span>

            <span slot="locale" slot-scope="locale">
              {{ locale | LOCALE_ }}
            </span>

            <span slot="note" slot-scope="note">
              <div class="sticker">{{ note || 'N/A' }}</div>
            </span>

            <span slot="created_at" slot-scope="created_at">
              <span> {{ created_at | moment('l') }} </span>
            </span>

            <span slot="created_by" slot-scope="created_by">
              <span> {{ created_by ? created_by.display_name : '---' }} </span>
            </span>

            <span slot="actions" slot-scope="customer">
              <a-button type="primary" class="ml-1" @click="onEdit(customer._id)" size="small" icon="edit"
                >Edit</a-button
              >
            </span>
          </a-table>
          <!-- Skeleton -->
          <ListSkeleton v-show="loading" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as staffsService from '@/services/system/staffs'
import ListSkeleton from '@/components/reno/skeleton/ListSkeleton.vue'
import store from '@/store'
import options from '@/options'
import router from '@/router'

const KEY = 'customers'

export default {
  components: {
    ListSkeleton,
  },
  data() {
    return {
      staffs: [],
      columns: [
        {
          title: this.$t('crm.list.avatar'),
          dataIndex: 'avatar',
          key: 'avatar',
          align: 'center',
          width: 80,
          fixed: 'left',

          scopedSlots: { customRender: 'avatar' },
        },
        {
          title: 'ID',
          dataIndex: 'uid',
          key: 'uid',
          fixed: 'left',
          width: 70,
          scopedSlots: { customRender: 'id' },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.uid - b.uid,
        },
        {
          title: this.$t('crm.list.gender'),
          dataIndex: 'gender',
          key: 'gender',
          fixed: 'left',
          width: 80,
          align: 'center',
          filters: [
            { text: 'Male', value: 'male' },
            { text: 'Female', value: 'female' },
          ],
          filterMultiple: false,
          scopedSlots: { customRender: 'gender' },
        },
        {
          title: this.$t('crm.list.fullname'),
          key: 'name',
          fixed: 'left',
          width: 230,
          scopedSlots: { customRender: 'name' },
        },
        {
          title: this.$t('crm.list.status'),
          dataIndex: 'status',
          key: 'status',
          align: 'center',
          width: 100,
          filters: options.CUSTOMER_STATUS.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: false,
          scopedSlots: { customRender: 'status' },
        },
        {
          title: this.$t('crm.list.projects'),
          dataIndex: 'projects',
          key: 'projects',
          align: 'center',
          width: 80,
          scopedSlots: { customRender: 'projects' },
        },
        {
          title: this.$t('crm.list.phone'),
          dataIndex: 'phone',
          key: 'phone',
          width: 130,
        },
        {
          title: this.$t('crm.list.email'),
          dataIndex: 'email',
          key: 'email',
          width: 180,
          ellipsis: true,
        },
        {
          title: 'WeChat',
          dataIndex: 'wechat',
          key: 'wechat',
          width: 130,
          ellipsis: true,
        },
        {
          title: this.$t('crm.list.location'),
          dataIndex: 'location',
          key: 'location',
          width: 160,
        },
        {
          title: this.$t('crm.list.locale'),
          dataIndex: 'locale',
          key: 'locale',
          width: 100,
          filters: options.LOCALES.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: true,
          scopedSlots: { customRender: 'locale' },
        },
        {
          title: this.$t('crm.list.note'),
          dataIndex: 'note',
          key: 'note',
          width: 220,
          scopedSlots: { customRender: 'note' },
        },
        {
          title: this.$t('crm.list.created'),
          dataIndex: 'created_at',
          key: 'created_at',
          width: 120,
          scopedSlots: { customRender: 'created_at' },
        },
        {
          title: this.$t('crm.list.active'),
          dataIndex: 'active',
          key: 'active',
          align: 'center',
          width: 100,
          filters: [
            {
              text: 'Yes',
              value: 'true',
            },
            {
              text: 'No',
              value: 'false',
            },
          ],
          filterMultiple: false,
          onFilter: (active, customer) => {
            return active === customer.active ? 'true' : 'false'
          },
          scopedSlots: { customRender: 'active' },
        },
        {
          title: this.$t('crm.list.creator'),
          dataIndex: 'created_by',
          key: 'created_by',
          width: 120,
          filters: [],
          scopedSlots: { customRender: 'created_by' },
        },
        {
          title: this.$t('crm.list.actions'),
          key: 'actions',
          fixed: 'right',
          width: 100,
          scopedSlots: { customRender: 'actions' },
        },
      ],
    }
  },
  methods: {
    async fetch() {
      await this.$store.dispatch('crm/fetchCustomers')
    },
    onNewCustomer() {
      this.$store.dispatch('crm/open', 'CREATE_CUSTOMER')
    },
    onEdit(id) {
      this.$router.push({ name: 'customer-detail', params: { id } })
    },
    updateQuery(pagination, filters, sorter) {
      this.$store.dispatch('crm/updateQuery', { key: KEY, query: { pagination, filters, sorter } })
    },
  },
  computed: {
    customers() {
      return this.$store.getters['crm/data'](KEY)
    },
    loading() {
      return this.$store.getters['crm/loading'](KEY)
    },
    pagination() {
      return this.$store.getters['crm/pagination'](KEY)
    },
    meta() {
      return this.$store.getters['crm/meta'](KEY)
    },
  },
  async created() {
    await this.fetch()
    this.staffs = await staffsService.fetchAllStaffs()
    if (this.staffs) {
      this.columns.find((c) => c.key === 'created_by').filters = this.staffs.map((s) => {
        return { text: s.display_name, value: s._id }
      })
    }
  },
  mounted() {
    // console.log(this.customers)
  },
  destroyed() {
    // store.dispatch('crm/init')
  },
}
</script>

<style lang="scss" scoped>
.crm.customers {
  .gender {
    color: #666;

    &.male {
      color: rgb(0, 81, 255);
    }
    &.female {
      color: rgb(255, 0, 0);
    }
  }

  .dot {
    display: inline-block;
    height: 0.86rem;
    width: 0.86rem;
    background: #666;
    border-radius: 20px;
    vertical-align: middle;
    position: relative;
    top: -2px;

    &.active {
      background: #00b44b;
    }
  }

  .sticker {
    background: #ffeb3b;
    padding: 2px 8px;
    color: #000;
    border-radius: 3px;
    font-size: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    box-shadow: 0 0 10px #eee;
  }
}
</style>
